import { Injectable, Signal, signal } from '@angular/core';
import { App as CapApp } from '@capacitor/app';
import { DateTime } from 'luxon';
import { AppVersion } from 'app/version';
import { markCallerReportsFailure } from 'app/fetch-failure-classifier';
import { IOS_APP_STORE_ITMS_URL } from '@cf-shared/data-model/app-install-urls';
import { LoggingService } from './logging.service';
import { ConnectionState, OnlineStateService } from './online-state.service';
import {
  UpdateInstallState,
  VersionCheckBackend,
  VersionCheckResult,
} from './version-check.dispatch';
import { IosUpdateLookup, watchPageHidden } from './version-check.ios-lookup';

// iOS backend for VersionCheckService. Apple has no in-app update API, so
// detection queries Apple's public iTunes Lookup endpoint and compares
// its `version` to the running build; the "Update" action deep-links to
// the App Store listing for a manual install. All the decision logic
// (bundle-id gate, region, semver compare, 6h cache with in-flight dedup,
// failure handling) lives in the pure, dependency-injected
// version-check.ios-lookup.ts so it is unit-testable without Angular /
// Capacitor. This class is the thin wrapper that supplies the real
// runtime dependencies.
//
// See design-docs/in-app-update-prompt.md → iOS — iTunes Lookup.
@Injectable({ providedIn: 'root' })
export class VersionCheckIosBackend implements VersionCheckBackend {
  private readonly lookup: IosUpdateLookup;

  // Constant Idle: the iOS "Update" action hands off to the App Store
  // and leaves the app, so there is no in-app download state to show.
  // Present only to satisfy the cross-platform backend contract. See
  // design-docs/in-app-update-prompt.md §installState.
  readonly installState: Signal<UpdateInstallState> = signal(UpdateInstallState.Idle).asReadonly();

  constructor(private logging: LoggingService, private onlineState: OnlineStateService) {
    this.lookup = new IosUpdateLookup({
      // bundle id gates the network call — only the public prod bundle
      // has an App Store listing (see notification.service.ts for the
      // same CapApp.getInfo() wrap pattern).
      getBundleId: async () => (await CapApp.getInfo()).id,
      getRunningVersion: () => AppVersion,
      getLanguage: () => (typeof navigator !== 'undefined' ? navigator.language : undefined),
      fetchJson: (url, timeoutMs) => this.fetchJsonAsync(url, timeoutMs),
      now: () => DateTime.now(),
      trackProblem: (name, context) => this.logging.trackSilentProblem(name, context),
      // a fresh ping of our own server, the same check CloudFunctionsService runs
      // before reporting a network error. bounded by the ping's own timeout, so it
      // cannot hang. it runs after a timeout too: that timeout came from Apple's
      // server, while the ping goes to ours.
      isOfflineAsync: async () =>
        (await this.onlineState.verifyOnlineStatus(ConnectionState.Offline, 'suspected')) === ConnectionState.Offline,
      watchHidden: () => watchPageHidden(typeof document !== 'undefined' ? document : undefined),
    });
  }

  getStatusAsync(): Promise<VersionCheckResult> {
    return this.lookup.getStatusAsync();
  }

  async startUpdateAsync(_belowMinimum: boolean): Promise<void> {
    // Apple gives no in-app update flow — hand off to the App Store
    // listing. We assign the itms-apps:// URL to window.location so iOS
    // opens the native App Store app DIRECTLY. Going through
    // @capacitor/browser with the https apps.apple.com URL would render
    // the listing as a web page in an in-app Safari view first, then hand
    // off — a visible flicker. The WKWebView passes the unknown scheme to
    // the OS (same mechanism the app's mailto: links rely on); the webview
    // itself does not navigate. This backend only ever runs on
    // Capacitor-iOS, so the scheme is always handled. We can never detect
    // when the user actually installs.
    window.location.href = IOS_APP_STORE_ITMS_URL;
  }

  // The "Please update" alert's Update button (#6770): iOS has no stronger flow than
  // the App Store hand-off, so it is the same.
  async startRequiredUpdateAsync(belowMinimum: boolean): Promise<void> {
    await this.startUpdateAsync(belowMinimum);
  }

  // No-op: iOS hands off to the App Store and can never reach the Ready
  // state, so there is nothing to "complete". Present only to satisfy
  // the cross-platform backend contract.
  async completeUpdateAsync(_belowMinimum: boolean): Promise<void> {
    // intentionally empty — see installState above
  }

  // Plain fetch() with a hard timeout. On native iOS the CapacitorHttp
  // plugin (enabled in capacitor.config.ts) intercepts window.fetch and
  // routes it through native HTTP, bypassing CORS. AbortController is the
  // primary cancel path, but the native interceptor does not reliably
  // honor the abort signal on iOS — so we also race the fetch against an
  // independent timeout promise. That guarantees the timeoutMs bound on
  // every platform: a hung native request can't outlive it. (We still
  // call abort() so the underlying request is cancelled where the signal
  // IS honored.) Rejects on timeout / non-200 / network error / JSON-parse
  // failure — the orchestrator treats every rejection as a lookup failure
  // (no update + silent problem).
  private async fetchJsonAsync(url: string, timeoutMs: number): Promise<unknown> {
    const controller = new AbortController();
    let timer: ReturnType<typeof setTimeout> | undefined;
    const timeout = new Promise<never>((_resolve, reject) => {
      timer = setTimeout(() => {
        controller.abort();
        reject(new Error(`iTunes Lookup timed out after ${timeoutMs}ms`));
      }, timeoutMs);
    });
    try {
      // marked so the global fetch wrapper only logs a failure: the lookup reports its own
      // failures (VersionCheckIosLookupFailed), so a wrapper report would be a second one
      const response = await Promise.race([
        fetch(url, markCallerReportsFailure({ signal: controller.signal })),
        timeout,
      ]);
      // race won by fetch — stop the abort timer before reading the body so
      // a late timeout can't abort an already-arrived successful response.
      clearTimeout(timer);
      if (!response.ok) {
        throw new Error(`iTunes Lookup HTTP ${response.status}`);
      }
      return await response.json();
    } finally {
      clearTimeout(timer);
    }
  }
}
